import {
  AloftPrice,
  AloftPriceJSON,
  SourceType,
  SubjectData,
  formatUnitRateForDisplay,
  isRegressionModelReliableForFeature,
  isSensitivityModelReliableForFeature,
  orUndefined,
  schSourceType,
  unitRateUsesCents,
} from '@aloft/toolkit-shared';
import { Block, Flex, ProductColor, Typography, theme } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra, aloft/prefer-sound-ui-button
import { Button, Popover, PopoverContent, PopoverTrigger } from '@aloft/toolkit-sui/chakra';

import React, { useRef, useState } from 'react';
import { Controller, ControllerRenderProps, FieldPath, useWatch } from 'react-hook-form';

import { dispatchAdjustmentsPageAlertTrigger } from '../../../modules/alerts/dispatchAdjustmentsPageAlertTrigger.ts';
import { useAlertsRef } from '../../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectCommentaryV2 } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { featuresSelectors } from '../../../redux/features/selectors/featuresSelectors.ts';
import { selectUnitRates } from '../../../redux/features/selectors/unitRatesSelectors/selectUnitRates.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { PropertyGridForm } from '../types.ts';
import {
  getDataSourceDisplayInfo,
  getSelectSourceTooltip,
  handleUpdatePropertyData,
} from '../utils.ts';
import { PopoverInputHeader } from './shared/PopoverInputHeader.tsx';
import { PopoverOptionsList } from './shared/PopoverOptionsList.tsx';
import { SourceIconSlot } from './shared/SourceIconSlot.tsx';
import { usePropertyGridCellFocus } from './shared/usePropertyGridCellFocus.ts';
// eslint-disable-next-line aloft/prefer-sound-ui-button
import { alertTargetFieldHoverSx, readOnlyFieldProps, sharedButtonStyle } from './styles.ts';
import { PropertyGridFieldProps } from './types.ts';
import { stripDollarSign } from './utils.tsx';

const hasEffectiveUnitRateValue = (
  displayRate: AloftPrice | undefined,
  isFocused: boolean,
  rawInputValue: string | undefined,
) => {
  if (displayRate != null) return true;
  if (!isFocused || rawInputValue === undefined) return false;
  const trimmed = rawInputValue.trim();
  return trimmed.length > 0 && trimmed !== '-' && trimmed !== '-$';
};

type SharedEditableUnitRateFieldProps = PropertyGridFieldProps & {
  unitRateFieldName: FieldPath<PropertyGridForm>;
  displayRate?: AloftPrice;
  storedUnitRate?: AloftPriceJSON | null;
  rawInputValue?: string;
  setRawInputValue: React.Dispatch<React.SetStateAction<string | undefined>>;
  isFocused: boolean;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  handleSetFieldValue: (
    onChange: ControllerRenderProps['onChange'],
    newValue?: string | null,
  ) => void;
  dollarsOnly: boolean;
  isAlertStyled: boolean;
  alertStyleVariant: 'error' | 'warning';
  isAlertFocusStyled: boolean;
  alertCellTooltip?: string;
  fieldRef: React.RefObject<HTMLElement>;
};

type UseUnitRateInputProps = Pick<
  SharedEditableUnitRateFieldProps,
  | 'displayRate'
  | 'storedUnitRate'
  | 'rawInputValue'
  | 'setRawInputValue'
  | 'isFocused'
  | 'setIsFocused'
  | 'handleSetFieldValue'
  | 'dollarsOnly'
>;

// Shared hook — input state + event handlers
const useUnitRateInput = ({
  displayRate,
  storedUnitRate,
  rawInputValue,
  setRawInputValue,
  isFocused,
  setIsFocused,
  handleSetFieldValue,
  dollarsOnly,
}: UseUnitRateInputProps) => {
  const rateString = displayRate?.toString(dollarsOnly);

  const inputValue = (() => {
    const v = isFocused && rawInputValue !== undefined ? rawInputValue : (rateString ?? '');
    return stripDollarSign(v);
  })();

  const handleFocus = () => {
    setIsFocused(true);
    const raw = rateString ? stripDollarSign(rateString) : '';
    setRawInputValue(raw);
  };

  const commitValue = (onChange: ControllerRenderProps['onChange']) => {
    const inputToParse = (rawInputValue ?? rateString ?? '').trim();
    try {
      if (!inputToParse || inputToParse === '-') {
        handleSetFieldValue(onChange, null);
      } else {
        handleSetFieldValue(onChange, inputToParse);
      }
    } catch {
      handleSetFieldValue(onChange, rateString);
    }
    setRawInputValue(undefined);
  };

  const handleBlur = (onChange: ControllerRenderProps['onChange'], onClose?: () => void) => {
    setIsFocused(false);
    commitValue(onChange);
    onClose?.();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    onChange: ControllerRenderProps['onChange'],
    onClose?: () => void,
  ) => {
    if (e.key === 'Escape') {
      setRawInputValue(undefined);
      setIsFocused(false);
      onChange(storedUnitRate);
      onClose?.();
    } else if (e.key === 'Enter' || e.key === 'NumpadEnter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  return { rateString, inputValue, handleFocus, handleBlur, handleKeyDown };
};

const labelTypographySx = { flexShrink: 0, lineHeight: '1' } as const;

type UnitRateInputRowProps = {
  label: string;
  inputValue: string;
  inputRef: React.Ref<HTMLInputElement>;
  inputStyle?: React.CSSProperties;
  onFocus: () => void;
  onBlur: () => void;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
};

const UnitRateInputRow = ({
  label,
  inputValue,
  inputRef,
  inputStyle,
  onFocus,
  onBlur,
  onChange,
  onKeyDown,
}: UnitRateInputRowProps) => (
  <>
    <Typography variant="body2" inline color="textSecondary" __dangerDoNotUseSx={labelTypographySx}>
      $
    </Typography>
    <input
      ref={inputRef}
      type="text"
      value={inputValue}
      placeholder="-"
      style={{
        flex: 1,
        minWidth: 0,
        background: 'transparent',
        border: 'none',
        outline: 'none',
        lineHeight: '1',
        padding: '0',
        ...inputStyle,
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={(e) => onChange(e.target.value.replace(/^0+(?=\d)/, ''))}
      onKeyDown={onKeyDown}
    />
    <Typography variant="body2" inline color="textSecondary" __dangerDoNotUseSx={labelTypographySx}>
      {label}
    </Typography>
  </>
);

// Legacy variant (no popover, plain inline input)
const LegacyEditableUnitRateField = ({
  field,
  control,
  colSpan = 1,
  unitRateFieldName,
  displayRate,
  storedUnitRate,
  handleSetFieldValue,
  rawInputValue,
  setRawInputValue,
  isFocused,
  setIsFocused,
  dollarsOnly,
  isAlertStyled,
  alertStyleVariant,
  isAlertFocusStyled,
  alertCellTooltip,
  fieldRef,
}: SharedEditableUnitRateFieldProps) => {
  const label = field.unit ? `/${field.unit}` : '';
  const alertBackgroundColor: ProductColor =
    alertStyleVariant === 'error' ? 'alertErrorLight' : 'alertWarningLight';
  const alertBorderColor =
    alertStyleVariant === 'error' ? theme.colors.alertError : theme.colors.alertWarning;

  return (
    <Controller
      name={unitRateFieldName}
      control={control}
      render={({ field: { onChange, ref } }) => {
        const { inputValue, handleFocus, handleBlur, handleKeyDown } = useUnitRateInput({
          displayRate,
          storedUnitRate,
          rawInputValue,
          setRawInputValue,
          isFocused,
          setIsFocused,
          handleSetFieldValue,
          dollarsOnly,
        });

        const hasValue = displayRate != null;
        const alertInsetBoxShadow = `inset 0 0 0 1.5px ${alertBorderColor}`;
        const alertEmphasisBoxShadow = `${alertInsetBoxShadow}, ${alertTargetFieldHoverSx.boxShadow}`;
        let border = 'none';
        let boxShadow: string | undefined;
        if (isAlertStyled) {
          boxShadow = isAlertFocusStyled ? alertEmphasisBoxShadow : alertInsetBoxShadow;
        } else if (hasValue) {
          border = `1px dashed ${theme.colors.buttonDefault}`;
        }

        const backgroundColor: ProductColor = isAlertStyled ? alertBackgroundColor : 'textOffset';

        return (
          <Block
            ref={fieldRef as React.RefObject<HTMLDivElement>}
            width="100%"
            height="100%"
            gridColumn={`span ${colSpan}`}
            backgroundColor={backgroundColor}
            title={alertCellTooltip}
            __dangerDoNotUseSx={{
              display: 'flex',
              alignItems: 'center',
              border,
              boxShadow,
              outline: 'none',
              cursor: 'text',
              ...(isAlertFocusStyled ? { filter: alertTargetFieldHoverSx.filter } : {}),
              _focusWithin: isAlertStyled
                ? {
                    boxShadow: alertEmphasisBoxShadow,
                    filter: alertTargetFieldHoverSx.filter,
                    outline: 'none',
                  }
                : {
                    outline: '1.5px solid',
                    outlineOffset: '-1.5px',
                    outlineColor: theme.colors.buttonDefault,
                    background: theme.colors.surface3,
                    border: '1px solid transparent',
                  },
              _hover: isAlertStyled
                ? {
                    backgroundColor: theme.colors[alertBackgroundColor],
                    boxShadow: alertEmphasisBoxShadow,
                    filter: alertTargetFieldHoverSx.filter,
                  }
                : {
                    outline: '1.5px solid',
                    outlineOffset: '-1.5px',
                    outlineColor: theme.colors.buttonDefault,
                  },
            }}
          >
            <UnitRateInputRow
              label={label}
              inputValue={inputValue}
              inputRef={ref}
              inputStyle={{
                fontSize: '12px',
                color: displayRate?.getDisplayColor() as ProductColor | undefined,
                padding: '0 2px',
                textAlign: 'left',
              }}
              onFocus={handleFocus}
              onBlur={() => handleBlur(onChange)}
              onChange={setRawInputValue}
              onKeyDown={(e) => handleKeyDown(e, onChange)}
            />
          </Block>
        );
      }}
    />
  );
};

// With-sources variant (popover + source selector)
const EditableUnitRateFieldWithSources = ({
  columnIndex,
  field,
  control,
  getValues,
  setValue,
  colSpan = 1,
  unitRateFieldName,
  selectedSourceFieldName,
  displayRate,
  storedUnitRate,
  handleSetFieldValue,
  rawInputValue,
  setRawInputValue,
  isFocused,
  setIsFocused,
  dollarsOnly,
  isAlertStyled,
  alertStyleVariant,
  isAlertFocusStyled,
  alertCellTooltip,
  fieldRef,
}: SharedEditableUnitRateFieldProps & {
  selectedSourceFieldName: FieldPath<PropertyGridForm>;
}) => {
  const unitRateKey = field.unitRateKey!;
  const label = field.unit ? `/${field.unit}` : '';
  const alertBackgroundColor: ProductColor =
    alertStyleVariant === 'error' ? 'alertErrorLight' : 'alertWarningLight';
  const alertBorderColor =
    alertStyleVariant === 'error' ? theme.colors.alertError : theme.colors.alertWarning;

  const propertyId = useWatch({ control, name: `columns.${columnIndex}.propertyId` });

  const selectedSourceWatched = useWatch({ control, name: selectedSourceFieldName }) as
    | { source?: SourceType }
    | undefined;

  const customInput = useRef<HTMLInputElement>(
    null,
  ) as React.MutableRefObject<HTMLInputElement | null>;

  const columns = getValues('columns');

  const unitRateModels = useAppSelector(selectUnitRates);

  const modelOptions = columns
    .filter(
      (column) =>
        column?.propertyId === propertyId &&
        column?.type !== 'property' &&
        schSourceType.safeParse(column?.type).success,
    )
    .map((column) => {
      const source = column.type as SourceType;
      const dataSourceColumnIndex = columns.findIndex(
        (col) => col?.propertyId === propertyId && col?.type === source,
      );
      if (dataSourceColumnIndex === -1) return null;

      const optionUnitRate = getValues(
        `columns.${dataSourceColumnIndex}.propertyData.editableData.salesComparison.unitRates.${unitRateKey}` as FieldPath<PropertyGridForm>,
      ) as AloftPriceJSON | undefined;

      const parsedRate = orUndefined(AloftPrice.schema.parse)(optionUnitRate);
      if (!parsedRate) return null;

      return {
        value: formatUnitRateForDisplay(parsedRate, unitRateKey),
        rawValue: parsedRate,
        source,
      };
    })
    .filter(
      (option): option is { value: string; rawValue: AloftPrice; source: SourceType } =>
        option !== null,
    );

  return (
    <Controller
      name={unitRateFieldName}
      control={control}
      render={({ field: { onChange, ref } }) => {
        const handleSetFieldValueWithSource: typeof handleSetFieldValue = (
          onChangeFn,
          newValue,
        ) => {
          let matchedSource: SourceType | undefined;
          if (newValue) {
            try {
              const typed = AloftPrice.fromDollarLike(newValue);
              matchedSource = modelOptions.find((opt) => opt.rawValue.equals(typed))?.source;
            } catch {
              // Unparsable input — leave matchedSource undefined
            }
          }

          if (matchedSource) {
            setValue(selectedSourceFieldName, { source: matchedSource });
          } else {
            setValue(selectedSourceFieldName, undefined);
          }
          handleSetFieldValue(onChangeFn, newValue);
        };

        const { rateString, inputValue, handleFocus, handleBlur, handleKeyDown } = useUnitRateInput(
          {
            displayRate,
            storedUnitRate,
            rawInputValue,
            setRawInputValue,
            isFocused,
            setIsFocused,
            handleSetFieldValue: handleSetFieldValueWithSource,
            dollarsOnly,
          },
        );

        const selectedSourceValue = selectedSourceWatched?.source;

        const liveMatchedSource: SourceType | undefined = (() => {
          if (!isFocused || !rawInputValue) return undefined;
          try {
            const typed = AloftPrice.fromDollarLike(rawInputValue);
            return modelOptions.find((opt) => opt.rawValue.equals(typed))?.source;
          } catch {
            return undefined;
          }
        })();
        const displayedSource = isFocused ? liveMatchedSource : selectedSourceValue;
        const hasValue = displayRate != null;
        const parsedSource = schSourceType.safeParse(displayedSource);

        const hasSelectedSource = parsedSource.success;
        const isCustomSource = hasValue && !hasSelectedSource;
        const isRegressionSource = displayedSource === 'regression';
        const isSensitivitySource = displayedSource === 'sensitivity';
        const showWarning =
          (isRegressionSource &&
            !isRegressionModelReliableForFeature(unitRateModels, unitRateKey)) ||
          (isSensitivitySource &&
            !isSensitivityModelReliableForFeature(unitRateModels, unitRateKey));

        let backgroundColor: ProductColor = 'surface1';
        if (isAlertStyled) {
          backgroundColor = alertBackgroundColor;
        } else if (showWarning) {
          backgroundColor = 'alertWarningLight';
        }

        const alertInsetBoxShadow = `inset 0 0 0 2px ${alertBorderColor}`;
        const alertEmphasisBoxShadow = `${alertInsetBoxShadow}, ${alertTargetFieldHoverSx.boxShadow}`;
        let border = 'none';
        let boxShadow: string | undefined;
        if (isAlertStyled) {
          boxShadow = isAlertFocusStyled ? alertEmphasisBoxShadow : alertInsetBoxShadow;
        } else if (isCustomSource) {
          border = `1px dashed ${theme.colors.buttonDefault}`;
        }

        const icon = hasSelectedSource
          ? getDataSourceDisplayInfo(parsedSource.data).icon
          : 'user-input';
        const baseTooltipLabel = hasSelectedSource
          ? getSelectSourceTooltip(parsedSource.data)
          : 'Custom';

        return (
          <div
            ref={fieldRef as React.RefObject<HTMLDivElement>}
            style={{ position: 'relative', gridColumn: `span ${colSpan}` }}
          >
            <Popover gutter={-32} initialFocusRef={customInput} variant="inline" isLazy matchWidth>
              {({ onClose }) => {
                const handleOnClose = () => {
                  if (document.activeElement instanceof HTMLElement) document.activeElement?.blur();
                  onClose();
                };

                return (
                  <>
                    <PopoverTrigger>
                      <Button
                        {...sharedButtonStyle}
                        backgroundColor={backgroundColor}
                        width="100%"
                        border={border}
                        boxShadow={boxShadow}
                        title={alertCellTooltip}
                        {...(isAlertFocusStyled ? { filter: alertTargetFieldHoverSx.filter } : {})}
                        _hover={
                          isAlertStyled
                            ? {
                                backgroundColor: theme.colors[alertBackgroundColor],
                                boxShadow: alertEmphasisBoxShadow,
                                filter: alertTargetFieldHoverSx.filter,
                              }
                            : sharedButtonStyle._hover
                        }
                      >
                        <Flex
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                          gap="4px"
                        >
                          <Flex
                            alignItems="center"
                            gap="4px"
                            minWidth="0"
                            flex={1}
                            overflow="hidden"
                          >
                            <Typography
                              variant="body2"
                              inline
                              color="textSecondary"
                              __dangerDoNotUseSx={labelTypographySx}
                            >
                              $
                            </Typography>
                            <Typography
                              variant="body2"
                              inline
                              truncate
                              color={displayRate?.getDisplayColor() as ProductColor | undefined}
                              __dangerDoNotUseSx={{
                                whiteSpace: 'nowrap',
                                flexShrink: 1,
                                minWidth: 0,
                                lineHeight: '1',
                              }}
                            >
                              {rateString ? stripDollarSign(rateString) : '-'}
                            </Typography>
                            <span style={{ flex: 1 }} />
                            <Typography
                              variant="body2"
                              inline
                              color="textSecondary"
                              __dangerDoNotUseSx={labelTypographySx}
                            >
                              {label}
                            </Typography>
                          </Flex>
                          <Flex
                            alignItems="center"
                            gap="0.25rem"
                            onClick={(e) => showWarning && hasValue && e.stopPropagation()}
                          >
                            <SourceIconSlot
                              show={hasValue}
                              icon={icon}
                              tooltipLabel={baseTooltipLabel}
                            />
                          </Flex>
                        </Flex>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent borderRadius="0" width="100%">
                      <PopoverInputHeader inputFocused={isFocused}>
                        <Flex alignItems="center" gap="4px" minWidth="0" flex={1}>
                          <UnitRateInputRow
                            label={label}
                            inputValue={inputValue}
                            inputRef={(instance) => {
                              ref(instance);
                              customInput.current = instance;
                            }}
                            inputStyle={{ fontSize: '14px' }}
                            onFocus={handleFocus}
                            onBlur={() => handleBlur(onChange, handleOnClose)}
                            onChange={setRawInputValue}
                            onKeyDown={(e) => handleKeyDown(e, onChange, onClose)}
                          />
                        </Flex>
                        <SourceIconSlot
                          show={hasValue}
                          icon={icon}
                          tooltipLabel={baseTooltipLabel}
                        />
                      </PopoverInputHeader>
                      <PopoverOptionsList
                        options={modelOptions}
                        onSelect={(option) => {
                          const matched = modelOptions.find((o) => o.source === option.source);
                          if (!matched) return;

                          setValue(selectedSourceFieldName, { source: option.source });
                          handleSetFieldValue(onChange, matched.rawValue.toDollarLike().toString());
                          handleOnClose();
                        }}
                      />
                    </PopoverContent>
                  </>
                );
              }}
            </Popover>
          </div>
        );
      }}
    />
  );
};

const EditableUnitRateFieldInner = ({
  columnIndex,
  field,
  control,
  getValues,
  setValue,
  colSpan = 1,
}: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();
  const alertsRef = useAlertsRef();
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;

  const { selectShouldShowAdjustmentRegression } = featuresSelectors;
  const shouldShowAdjustmentRegression = useAppSelector(selectShouldShowAdjustmentRegression);

  const unitRateKey = field.unitRateKey!;
  const forceNegative = field.manualAdjustmentKey === 'actualAge' && unitRateKey === 'actualAge';

  const propertyId = useWatch({ control, name: `columns.${columnIndex}.propertyId` });
  const unitRateFieldName =
    `columns.${columnIndex}.propertyData.editableData.salesComparison.unitRates.${unitRateKey}` as FieldPath<PropertyGridForm>;
  const selectedSourceFieldName =
    `columns.${columnIndex}.propertyData.selectedSources.editableData.salesComparison.unitRates.${unitRateKey}` as FieldPath<PropertyGridForm>;

  const storedUnitRate = useWatch({ control, name: unitRateFieldName }) as
    | AloftPriceJSON
    | null
    | undefined;

  const [rawInputValue, setRawInputValue] = useState<string | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);

  const displayRate = orUndefined(AloftPrice.schema.parse)(storedUnitRate ?? undefined);
  const dollarsOnly = !unitRateUsesCents(unitRateKey);

  const fieldRef = useRef<HTMLDivElement>(null);
  const {
    isAlertHighlighted,
    isAlertStyled,
    alertStyleVariant,
    alertCellTooltip,
    clearAlertTarget,
  } = usePropertyGridCellFocus({
    cellKeyId: unitRateKey,
    propertyId,
    fieldRef,
    isFieldComplete: hasEffectiveUnitRateValue(displayRate, isFocused, rawInputValue),
  });

  const handleSetFieldValue = (
    onChange: ControllerRenderProps['onChange'],
    newValue?: string | null,
  ) => {
    let storedRate: AloftPriceJSON | null | undefined;
    if (newValue === null) {
      storedRate = null;
    } else {
      let parsedRate = newValue ? AloftPrice.fromDollarLike(newValue) : undefined;
      if (parsedRate && forceNegative) {
        parsedRate = AloftPrice.fromUnitAmount(
          -Math.abs(parsedRate.unitAmount),
          parsedRate.currency,
        );
      }
      storedRate = parsedRate?.toJSON();
    }
    onChange(storedRate);

    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    const subjectPropertyData = propertyData as SubjectData | undefined;
    if (subjectPropertyData?.editableData) {
      if (!subjectPropertyData.editableData.salesComparison) {
        subjectPropertyData.editableData.salesComparison = { unitRates: {} };
      } else subjectPropertyData.editableData.salesComparison.unitRates ||= {};
      subjectPropertyData.editableData.salesComparison.unitRates![unitRateKey] = storedRate;
    }
    handleUpdatePropertyData(propertyId, getValues(), dispatch, subjectPropertyData);
    clearAlertTarget();
    dispatchAdjustmentsPageAlertTrigger(dispatch, alertsRef, alertsCommentary);
  };

  const sharedProps: SharedEditableUnitRateFieldProps = {
    columnIndex,
    field,
    control,
    getValues,
    setValue,
    colSpan,
    unitRateFieldName,
    displayRate,
    storedUnitRate,
    handleSetFieldValue,
    rawInputValue,
    setRawInputValue,
    isFocused,
    setIsFocused,
    dollarsOnly,
    isAlertStyled,
    alertStyleVariant,
    isAlertFocusStyled: isAlertHighlighted,
    alertCellTooltip,
    fieldRef,
  };

  if (shouldShowAdjustmentRegression) {
    return (
      <EditableUnitRateFieldWithSources
        {...sharedProps}
        selectedSourceFieldName={selectedSourceFieldName}
      />
    );
  }

  return <LegacyEditableUnitRateField {...sharedProps} />;
};

export const EditableUnitRateField = (props: PropertyGridFieldProps) => {
  const { field, colSpan = 1 } = props;
  const { unitRateKey } = field;

  if (!unitRateKey) return <Typography {...readOnlyFieldProps(colSpan)}>-</Typography>;
  return <EditableUnitRateFieldInner {...props} />;
};
